import React, { useMemo } from "react"
import { Table, Tag, Space, Typography, Result, Button } from "@mimber/ui-web"
import type { ColumnsType, TableProps } from "@mimber/ui-web"
import {
  handleTableChange,
  applyQueryParamsToColumns,
  formatTableDate,
} from "@mimber/ui-web"
import { useRememberedTableQueryParams } from "@/core/hooks"
import {
  getRememberedPaginationConfig,
  getRememberedTableLocale,
  getRememberedTableErrorAction,
} from "@/utils"
import { useDashboardRecentActivity } from "@mimber/client/mimber-organization"
import type { DashboardRecentActivityItemDto } from "@mimber/schema"
import { Resource, Action } from "@mimber/schema"
import type { RecentActivityTableProps } from "./RecentActivityTable.types"
import {
  RESOURCE_LABELS,
  ACTION_LABELS,
  ACTION_COLORS,
  getActivityIcon,
} from "./RecentActivityTable.utils"

const { Text } = Typography

export const RecentActivityTable: React.FunctionComponent<
  RecentActivityTableProps
> = ({ onView }) => {
  const { queryParams, setQueryParams, resetQueryParams } =
    useRememberedTableQueryParams({
      tableId: "recent-activity",
      defaultQueryParams: {
        page: 1,
        limit: 20,
      },
    })

  const { data, isLoading, isError, refetch } =
    useDashboardRecentActivity(queryParams)

  const items = data?.data ?? []
  const total = data?.meta?.pagination?.total ?? 0

  const columns: ColumnsType<DashboardRecentActivityItemDto> = useMemo(
    () => [
      {
        title: "Activity",
        dataIndex: "label",
        key: "label",
        render: (label: string, record) => (
          <Space>
            {getActivityIcon(record.resource)}
            <Text>{label}</Text>
          </Space>
        ),
      },
      {
        title: "Resource",
        dataIndex: "resource",
        key: "resource",
        filters: Object.entries(RESOURCE_LABELS).map(([resource, label]) => ({
          text: label,
          value: resource,
        })),
        render: (resource: Resource) => (
          <Tag>{RESOURCE_LABELS[resource] ?? resource}</Tag>
        ),
      },
      {
        title: "Action",
        dataIndex: "action",
        key: "action",
        filters: Object.entries(ACTION_LABELS).map(([action, label]) => ({
          text: label,
          value: action,
        })),
        render: (action: Action) => (
          <Tag color={ACTION_COLORS[action] ?? "default"}>
            {ACTION_LABELS[action] ?? action}
          </Tag>
        ),
      },
      {
        title: "Performed By",
        dataIndex: "performedBy",
        key: "performedBy",
        render: (performedBy: string | null) =>
          performedBy ? (
            <Text>{performedBy}</Text>
          ) : (
            <Text type="secondary">System</Text>
          ),
      },
      {
        title: "Date",
        dataIndex: "createdAt",
        key: "createdAt",
        sorter: true,
        sortDirections: ["ascend", "descend"] as const,
        render: (createdAt: string) => (
          <Text type="secondary">{formatTableDate(createdAt)}</Text>
        ),
      },
    ],
    []
  )

  const displayColumns = useMemo(
    () => applyQueryParamsToColumns(columns, queryParams),
    [columns, queryParams]
  )

  const onTableChange: TableProps<DashboardRecentActivityItemDto>["onChange"] =
    (pagination, filters, sorter) => {
      const tableChangeData = {
        pagination,
        filters: filters as Record<string, string[] | null>,
        sorter: {
          field: Array.isArray(sorter) ? sorter[0]?.field : sorter?.field,
          order: Array.isArray(sorter) ? sorter[0]?.order : sorter?.order,
        } as { field?: string | string[]; order?: "ascend" | "descend" },
      }
      handleTableChange(tableChangeData, setQueryParams, columns)
    }

  if (isError) {
    return (
      <Result
        status="error"
        title="Failed to load recent activity"
        subTitle="The recent activity feed could not be loaded."
        extra={
          <Space>
            {getRememberedTableErrorAction(resetQueryParams, queryParams)}
            <Button type="primary" onClick={() => void refetch()}>
              Retry
            </Button>
          </Space>
        }
      />
    )
  }

  return (
    <Table<DashboardRecentActivityItemDto>
      bordered
      columns={displayColumns}
      dataSource={items}
      loading={isLoading}
      rowKey="uuid"
      pagination={getRememberedPaginationConfig(
        total,
        queryParams,
        resetQueryParams
      )}
      locale={getRememberedTableLocale(resetQueryParams, queryParams)}
      onChange={onTableChange}
      onRow={(record) => ({
        onClick: () => onView?.(record),
        style: onView ? { cursor: "pointer" } : undefined,
      })}
    />
  )
}
